import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marketingAPI } from '../services/api';
import Icon from './Icon';

// Simple rich text editor toolbar button
const ToolbarButton = ({ icon, label, active, onClick, disabled, darkMode = true }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    style={{
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '8px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? 'var(--lime-primary)' : (darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'),
      color: active ? '#0a0a1a' : (darkMode ? 'rgba(255,255,255,0.8)' : '#374151'),
      fontSize: '16px',
      fontWeight: active ? '700' : '500',
      transition: 'all 0.2s',
      opacity: disabled ? 0.5 : 1
    }}
  >
    {icon}
  </button>
);

// Font size selector
const FontSizeSelector = ({ onChange, disabled, darkMode = true }) => (
  <select
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    style={{
      height: '36px',
      padding: '0 12px',
      border: darkMode ? 'none' : '1px solid #d1d5db',
      borderRadius: '8px',
      background: darkMode ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
      color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151',
      fontSize: '14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1
    }}
  >
    <option value="">Font Size</option>
    <option value="1">Small</option>
    <option value="3">Normal</option>
    <option value="5">Large</option>
    <option value="7">Extra Large</option>
  </select>
);

// Email template renderer
const EmailPreview = ({ subject, content, companyName = 'Your Company' }) => {
  const fullSubject = `Frame-Book Promo: ${subject || 'Your Subject'}`;
  
  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: "'Segoe UI', Arial, sans-serif",
      background: '#ffffff',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
    }}>
      {/* Email client header */}
      <div style={{
        background: '#f5f5f5',
        padding: '12px 20px',
        borderBottom: '1px solid #e0e0e0',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '4px' }}>
          <strong>From:</strong> {companyName} &lt;noreply@frame-book.com&gt;
        </div>
        <div>
          <strong>Subject:</strong> <span style={{ color: '#1a1a2e', fontWeight: '600' }}>{fullSubject}</span>
        </div>
      </div>

      {/* Email header with Frame-Book branding */}
      <div style={{
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 100%)',
        padding: '32px 24px',
        textAlign: 'center'
      }}>
        {/* Frame-Book Logo */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #a3e635 0%, #84cc16 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            <Icon name="email" customColor="#0a0a1a" size={24} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: '700',
              letterSpacing: '-0.5px'
            }}>
              Frame-Book
            </div>
            <div style={{
              color: '#4A8C18',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Your Bike's Digital Passport
            </div>
          </div>
        </div>
        
        {/* Company badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(163, 230, 53, 0.15)',
          border: '1px solid rgba(163, 230, 53, 0.3)',
          borderRadius: '20px',
          padding: '6px 16px',
          fontSize: '12px',
          color: '#a3e635',
          fontWeight: '500'
        }}>
          <Icon name="email" customColor="#a3e635" size={12} />
          Newsletter from <strong>{companyName}</strong>
        </div>
      </div>

      {/* Email content */}
      <div style={{
        padding: '32px 24px',
        background: '#ffffff',
        color: '#333333',
        lineHeight: '1.8'
      }}>
        <p style={{ 
          fontSize: '16px', 
          color: '#666',
          marginTop: 0,
          marginBottom: '24px'
        }}>
          Dear Valued Customer,
        </p>
        
        {/* Main content box */}
        <div style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderLeft: '4px solid #a3e635',
          borderRadius: '0 12px 12px 0',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div 
            style={{ 
              fontSize: '15px',
              color: '#1f2937'
            }}
            dangerouslySetInnerHTML={{ 
              __html: content || '<p style="color: #9ca3af; margin: 0;">Your email content will appear here...</p>' 
            }}
          />
        </div>

        {/* CTA Button */}
        <div style={{ textAlign: 'center', margin: '32px 0' }}>
          <a 
            href="#" 
            style={{
              display: 'inline-block',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #a3e635 0%, #84cc16 100%)',
              color: '#0a0a1a',
              textDecoration: 'none',
              borderRadius: '10px',
              fontWeight: '700',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(163, 230, 53, 0.3)'
            }}
          >
            Visit Our Workshop
          </a>
        </div>

        <p style={{ 
          fontSize: '15px',
          color: '#374151',
          marginBottom: 0
        }}>
          Best regards,<br />
          <strong>The {companyName} Team</strong>
        </p>
      </div>

      {/* Footer */}
      <div style={{
        background: '#1a1a2e',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#a5b4fc',
          marginBottom: '8px'
        }}>
          Powered by <strong style={{ color: '#4A8C18' }}>Frame-Book</strong> — Your Bike's Digital Passport
        </div>
        <div style={{
          fontSize: '11px',
          color: '#6b7280'
        }}>
          You're receiving this because you opted-in for marketing emails.
          <br />
          <a href="#" style={{ color: '#4A8C18', textDecoration: 'none' }}>Unsubscribe</a> | <a href="#" style={{ color: '#4A8C18', textDecoration: 'none' }}>Manage Preferences</a>
        </div>
      </div>
    </div>
  );
};

function MarketingCampaignManager({ companyId, darkMode = true }) {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Campaign status
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  
  // Available brands
  const [availableBrands, setAvailableBrands] = useState([]);
  
  // Recipients preview
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientStats, setRecipientStats] = useState({ total_connections: 0, excluded: {} });
  const [debugUsers, setDebugUsers] = useState([]);
  
  // Form state
  const [emailSubject, setEmailSubject] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  
  // Company info
  const [companyName, setCompanyName] = useState('');
  
  // Editor ref
  const editorRef = useRef(null);
  
  // View mode
  const [viewMode, setViewMode] = useState('editor'); // 'editor', 'preview', 'history'
  
  // Campaign history
  const [campaignHistory, setCampaignHistory] = useState([]);

  // Fetch initial data
  useEffect(() => {
    if (!companyId) return; // Don't fetch if no companyId
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statusRes, brandsRes, historyRes, templateRes] = await Promise.all([
          marketingAPI.getCampaignStatus(companyId),
          marketingAPI.getAvailableBrands(companyId),
          marketingAPI.getPromotionalEmails(),
          marketingAPI.getGlobalTemplate(companyId)
        ]);
        
        setCampaignStatus(statusRes.data);
        setCurrentCampaign(statusRes.data.current_campaign);
        setAvailableBrands(brandsRes.data.brands || []);
        setCampaignHistory(historyRes.data.promotional_emails || []);
        setCompanyName(templateRes.data.company_name || 'Your Company');
        
        // If there's a draft campaign, load its data
        if (statusRes.data.current_campaign && 
            ['draft', 'rejected'].includes(statusRes.data.current_campaign.status)) {
          const campaign = statusRes.data.current_campaign;
          // Remove "Frame-Book Promo: " prefix if present
          const subject = campaign.subject?.replace(/^Frame-Book Promo:\s*/i, '') || '';
          setEmailSubject(subject);
          setEditorContent(campaign.html_body || '');
          if (editorRef.current) {
            editorRef.current.innerHTML = campaign.html_body || '';
          }
        }
      } catch (err) {
        setError('Failed to load marketing data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [companyId]);

  // Preview recipients when filter changes
  useEffect(() => {
    if (!companyId) return; // Don't fetch if no companyId
    
    const previewRecipients = async () => {
      setLoadingRecipients(true);
      try {
        const res = await marketingAPI.previewRecipientsByFilter({
          company_id: companyId,
          send_to_all: sendToAll,
          filter_brand: sendToAll ? '' : selectedBrand
        });
        console.log('Recipients response:', res.data);
        setRecipients(res.data.recipients || []);
        setRecipientStats({
          total_connections: res.data.total_connections || 0,
          excluded: res.data.excluded || {}
        });
        setDebugUsers(res.data.debug_users || []);
      } catch (err) {
        console.error('Failed to preview recipients', err);
      } finally {
        setLoadingRecipients(false);
      }
    };
    
    previewRecipients();
  }, [companyId, sendToAll, selectedBrand]);

  // Update editorContent when editor changes
  const handleEditorChange = useCallback(() => {
    if (editorRef.current) {
      setEditorContent(editorRef.current.innerHTML);
    }
  }, []);

  // Editor commands
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleEditorChange();
  }, [handleEditorChange]);

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleUnderline = () => execCommand('underline');
  const handleFontSize = (size) => {
    if (size) execCommand('fontSize', size);
  };

  // Get full subject with prefix
  const getFullSubject = () => {
    if (!emailSubject.trim()) return '';
    return `Frame-Book Promo: ${emailSubject}`;
  };

  // Save draft
  const handleSaveDraft = async () => {
    if (!emailSubject.trim()) {
      setError('Please enter an email subject');
      return;
    }
    
    const htmlContent = editorRef.current?.innerHTML || '';
    if (!htmlContent.trim() || htmlContent === '<br>') {
      setError('Please enter email content');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const data = {
        subject: getFullSubject(),
        html_body: htmlContent,
        text_body: editorRef.current?.innerText || '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        send_to_all: sendToAll,
        filter_brand: sendToAll ? null : selectedBrand
      };
      
      if (currentCampaign && ['draft', 'rejected'].includes(currentCampaign.status)) {
        await marketingAPI.updatePromotionalEmail(currentCampaign.id, data);
        setSuccess('Draft updated successfully!');
      } else {
        const res = await marketingAPI.createPromotionalEmail(data);
        setCurrentCampaign(res.data.promotional_email);
        setSuccess('Draft saved successfully!');
      }
      
      // Refresh status
      const statusRes = await marketingAPI.getCampaignStatus();
      setCampaignStatus(statusRes.data);
      setCurrentCampaign(statusRes.data.current_campaign);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // Submit for approval
  const handleSubmitForApproval = async () => {
    if (!emailSubject.trim()) {
      setError('Please enter an email subject');
      return;
    }
    
    const htmlContent = editorRef.current?.innerHTML || '';
    if (!htmlContent.trim() || htmlContent === '<br>') {
      setError('Please enter email content');
      return;
    }
    
    // Save first if no current campaign
    if (!currentCampaign || !['draft', 'rejected'].includes(currentCampaign.status)) {
      setSaving(true);
      try {
        const data = {
          subject: getFullSubject(),
          html_body: htmlContent,
          text_body: editorRef.current?.innerText || '',
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          send_to_all: sendToAll,
          filter_brand: sendToAll ? null : selectedBrand
        };
        const res = await marketingAPI.createPromotionalEmail(data);
        await marketingAPI.submitPromotionalEmail(res.data.promotional_email.id);
        setSuccess('Campaign submitted for approval! You will be notified when the admin reviews it.');
        
        // Refresh
        const statusRes = await marketingAPI.getCampaignStatus();
        setCampaignStatus(statusRes.data);
        setCurrentCampaign(statusRes.data.current_campaign);
        const historyRes = await marketingAPI.getPromotionalEmails();
        setCampaignHistory(historyRes.data.promotional_emails || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to submit for approval');
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // Otherwise just submit existing
    try {
      setSaving(true);
      // Update with latest content first
      await marketingAPI.updatePromotionalEmail(currentCampaign.id, {
        subject: getFullSubject(),
        html_body: htmlContent,
        text_body: editorRef.current?.innerText || ''
      });
      await marketingAPI.submitPromotionalEmail(currentCampaign.id);
      setSuccess('Campaign submitted for approval! You will be notified when the admin reviews it.');
      
      // Refresh status
      const statusRes = await marketingAPI.getCampaignStatus();
      setCampaignStatus(statusRes.data);
      setCurrentCampaign(statusRes.data.current_campaign);
      const historyRes = await marketingAPI.getPromotionalEmails();
      setCampaignHistory(historyRes.data.promotional_emails || []);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit for approval');
    } finally {
      setSaving(false);
    }
  };

  // Delete draft
  const handleDeleteDraft = async () => {
    if (!currentCampaign?.id) return;
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    
    try {
      await marketingAPI.deletePromotionalEmail(currentCampaign.id);
      setCurrentCampaign(null);
      setEmailSubject('');
      setEditorContent('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      setSuccess('Draft deleted');
      
      // Refresh status
      const statusRes = await marketingAPI.getCampaignStatus();
      setCampaignStatus(statusRes.data);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete draft');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'rgba(107, 114, 128, 0.2)', color: '#9ca3af', label: 'Draft' },
      pending_approval: { bg: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', label: 'Pending Approval' },
      approved: { bg: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', label: 'Approved' },
      rejected: { bg: 'rgba(239, 68, 68, 0.2)', color: '#f87171', label: 'Rejected' },
      sent: { bg: 'rgba(26, 40, 17, 0.2)', color: '#4A8C18', label: 'Sent' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span style={{
        padding: '6px 14px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        background: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  // Get month name
  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  // Can edit
  const canEdit = !currentCampaign || ['draft', 'rejected'].includes(currentCampaign?.status);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px',
        color: 'rgba(255,255,255,0.6)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid rgba(255,255,255,0.1)',
            borderTopColor: 'var(--lime-primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading marketing tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .marketing-editor:focus {
          outline: none;
        }
        .marketing-editor p { margin: 0 0 12px 0; }
        .marketing-editor h1, .marketing-editor h2, .marketing-editor h3 {
          margin: 0 0 16px 0;
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            color: darkMode ? '#fff' : '#111827',
            fontSize: '24px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon name="email" color="primary" darkMode={darkMode} size={26} />
            Email Campaign
          </h2>
          <p style={{ 
            margin: 0, 
            color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563',
            fontSize: '14px'
          }}>
            Create beautiful marketing emails for your customers
          </p>
        </div>
        
        {/* Monthly status indicator */}
        <div style={{
          background: campaignStatus?.can_send_this_month 
            ? 'rgba(16, 185, 129, 0.15)' 
            : 'rgba(245, 158, 11, 0.15)',
          border: `1px solid ${campaignStatus?.can_send_this_month ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
          borderRadius: '12px',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Icon 
            name={campaignStatus?.can_send_this_month ? 'success' : 'calendar'} 
            color={campaignStatus?.can_send_this_month ? 'success' : 'warning'} 
            darkMode={true}
            size={20}
          />
          <div>
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '600',
              color: campaignStatus?.can_send_this_month ? '#4A8C18' : '#fbbf24'
            }}>
              {getMonthName(campaignStatus?.month)} {campaignStatus?.year}
            </div>
            <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
              {campaignStatus?.can_send_this_month 
                ? '1 campaign available' 
                : campaignStatus?.reason || 'Monthly limit reached'}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          color: '#f87171',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icon name="warning" color="error" darkMode={true} size={20} />
          {error}
          <button 
            onClick={() => setError('')}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: '#f87171',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          color: '#4A8C18',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icon name="success" color="success" darkMode={true} size={20} />
          {success}
        </div>
      )}

      {/* Current campaign status banner */}
      {currentCampaign && currentCampaign.status === 'pending_approval' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <Icon name="loading" color="warning" darkMode={true} size={48} />
          </div>
          <h3 style={{ margin: '0 0 8px 0', color: '#fbbf24', fontSize: '18px' }}>
            Awaiting Admin Approval
          </h3>
          <p style={{ margin: 0, color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '14px' }}>
            Your campaign has been submitted and is being reviewed by the administrator.
            <br />You'll be notified once it's approved and sent.
          </p>
        </div>
      )}

      {currentCampaign && currentCampaign.status === 'rejected' && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <Icon name="error" color="error" darkMode={true} size={32} />
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#f87171', fontSize: '16px' }}>
                Campaign Rejected
              </h3>
              <p style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563', fontSize: '14px' }}>
                {currentCampaign.rejection_reason || 'No reason provided'}
              </p>
              <p style={{ margin: 0, color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', fontSize: '13px' }}>
                Please make the necessary changes and submit again.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View mode tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
        paddingBottom: '12px'
      }}>
        {[
          { id: 'editor', label: 'Create Email', iconName: 'edit' },
          { id: 'preview', label: 'Preview', iconName: 'view' },
          { id: 'history', label: 'History', iconName: 'details' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              // Save editor content before switching to preview
              if (viewMode === 'editor' && tab.id === 'preview' && editorRef.current) {
                const currentContent = editorRef.current.innerHTML;
                setEditorContent(currentContent);
              }
              // Restore editor content when switching back from preview
              if (viewMode === 'preview' && tab.id === 'editor') {
                // Use setTimeout to ensure the DOM is ready
                setTimeout(() => {
                  if (editorRef.current && editorContent) {
                    editorRef.current.innerHTML = editorContent;
                  }
                }, 0);
              }
              setViewMode(tab.id);
            }}
            style={{
              padding: '10px 20px',
              border: viewMode === tab.id ? 'none' : (darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)'),
              borderRadius: '10px',
              background: viewMode === tab.id ? '#1A2811' : 'transparent',
              color: viewMode === tab.id ? '#ffffff' : (darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: viewMode === tab.id ? '0 6px 16px rgba(26, 40, 17, 0.3)' : 'none'
            }}
          >
            <Icon
              name={tab.iconName}
              customColor={viewMode === tab.id ? '#ffffff' : (darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)')}
              size={16}
            />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Editor View */}
      {viewMode === 'editor' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
          {/* Main editor */}
          <div style={{
            background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            {/* Subject input */}
            <div style={{
              padding: '20px',
              borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb'
            }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Email Subject
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db',
                borderRadius: '10px',
                background: darkMode ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                overflow: 'hidden'
              }}>
                <span style={{
                  padding: '14px 16px',
                  background: darkMode ? 'rgba(163, 230, 53, 0.15)' : '#7B9663',
                  color: darkMode ? '#a3e635' : '#ffffff',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  borderRight: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db'
                }}>
                  Frame-Book Promo:
                </span>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={!canEdit}
                  placeholder="Your catchy subject line..."
                  style={{
                    flex: 1,
                    padding: '14px 16px',
                    border: 'none',
                    background: 'transparent',
                    color: darkMode ? '#fff' : '#1f2937',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Toolbar */}
            <div style={{
              padding: '12px 20px',
              background: darkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb',
              borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <ToolbarButton 
                icon="B" 
                label="Bold (Ctrl/⌘+B)" 
                onClick={handleBold}
                disabled={!canEdit}
                darkMode={darkMode}
              />
              <ToolbarButton 
                icon="I" 
                label="Italic (Ctrl/⌘+I)" 
                onClick={handleItalic}
                disabled={!canEdit}
                darkMode={darkMode}
              />
              <ToolbarButton 
                icon="U" 
                label="Underline (Ctrl/⌘+U)" 
                onClick={handleUnderline}
                disabled={!canEdit}
                darkMode={darkMode}
              />
              <div style={{
                width: '1px',
                height: '24px',
                background: darkMode ? 'rgba(255,255,255,0.1)' : '#d1d5db',
                margin: '0 8px'
              }} />
              <FontSizeSelector onChange={handleFontSize} disabled={!canEdit} darkMode={darkMode} />
              
              <div style={{
                marginLeft: 'auto',
                fontSize: '12px',
                color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af'
              }}>
                Format: bold, italic, underline, font size
              </div>
            </div>

            {/* Content editor */}
            <div style={{ padding: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Email Content
              </label>
              <div
                ref={editorRef}
                className="marketing-editor"
                contentEditable={canEdit}
                onInput={handleEditorChange}
                onBlur={handleEditorChange}
                style={{
                  minHeight: '300px',
                  padding: '20px',
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db',
                  borderRadius: '10px',
                  background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                  color: darkMode ? '#fff' : '#1f2937',
                  fontSize: '15px',
                  lineHeight: '1.7',
                  cursor: canEdit ? 'text' : 'not-allowed',
                  opacity: canEdit ? 1 : 0.7
                }}
                suppressContentEditableWarning={true}
              />
              <p style={{ 
                margin: '12px 0 0 0', 
                fontSize: '12px', 
                color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Icon name="info" color="info" darkMode={darkMode} size={12} />
                Tip: Write your marketing message here. Use the toolbar above to format your text.
              </p>
            </div>

            {/* Action buttons */}
            <div style={{
              padding: '20px',
              background: darkMode ? 'rgba(0,0,0,0.2)' : '#f3f4f6',
              borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              {currentCampaign && ['draft', 'rejected'].includes(currentCampaign.status) && (
                <button
                  onClick={handleDeleteDraft}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '10px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#f87171',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Delete Draft
                </button>
              )}
              
              {canEdit && (
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  style={{
                    padding: '12px 24px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #d1d5db',
                    borderRadius: '10px',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    color: darkMode ? '#fff' : '#1f2937',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
              )}
              
              {canEdit && (
                <button
                  onClick={handleSubmitForApproval}
                  disabled={saving || recipients.length === 0}
                  style={{
                    padding: '12px 28px',
                    border: 'none',
                    borderRadius: '10px',
                    background: recipients.length === 0 
                      ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb')
                      : 'linear-gradient(135deg, var(--lime-primary) 0%, #a3e635 100%)',
                    color: recipients.length === 0 ? (darkMode ? 'rgba(255,255,255,0.4)' : '#6b7280') : '#ffffff',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: saving || recipients.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon name="send" color="primary" darkMode={true} size={16} />
                  Submit for Approval
                </button>
              )}
            </div>
          </div>

          {/* Right sidebar - Recipients */}
          <div style={{
            background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            borderRadius: '16px',
            padding: '20px',
            height: 'fit-content'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '15px',
              fontWeight: '600',
              color: darkMode ? '#fff' : '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon name="users" color="primary" darkMode={darkMode} size={18} />
              Recipients
            </h3>

            {/* Send to all toggle */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '22px 1fr',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
            onClick={() => setSendToAll(prev => !prev)}
            role="button"
            aria-label="Toggle send to all customers"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSendToAll(prev => !prev);
              }
            }}>
              <input
                type="checkbox"
                id="sendToAll"
                checked={sendToAll}
                onChange={(e) => setSendToAll(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: 'var(--lime-primary)',
                  margin: 0,
                  cursor: 'pointer'
                }}
              />
              <label
                style={{ 
                  color: darkMode ? '#fff' : '#1f2937', 
                  fontSize: '14px',
                  cursor: 'pointer',
                  lineHeight: '1.4',
                  width: '100%'
                }}
              >
                Send to all customers
              </label>
            </div>

            {/* Brand filter */}
            {!sendToAll && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                  marginBottom: '8px'
                }}>
                  Filter by Bike Brand
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #d1d5db',
                    borderRadius: '10px',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    color: darkMode ? '#fff' : '#1f2937',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a brand...</option>
                  {availableBrands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Recipients count */}
            <div style={{
              background: recipients.length > 0 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${recipients.length > 0 
                ? 'rgba(16, 185, 129, 0.2)' 
                : 'rgba(239, 68, 68, 0.2)'}`,
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '700',
                color: recipients.length > 0 ? '#4A8C18' : '#f87171'
              }}>
                {loadingRecipients ? '...' : recipients.length}
              </div>
              <div style={{
                fontSize: '13px',
                color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563'
              }}>
                {recipients.length === 1 ? 'recipient' : 'recipients'}
              </div>
            </div>

            {recipients.length === 0 && !loadingRecipients && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                fontSize: '13px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <Icon name="warning" color="warning" darkMode={true} size={24} />
                </div>
                {!sendToAll && !selectedBrand 
                  ? 'Select a brand to filter recipients'
                  : 'No customers have opted-in for marketing emails yet'}
              </div>
            )}

            {/* Debug info for excluded users */}
            {!loadingRecipients && recipientStats.total_connections > 0 && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
                borderRadius: '8px',
                fontSize: '12px',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
              }}>
                <div style={{ marginBottom: '6px', fontWeight: '500', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="analytics" color="info" darkMode={darkMode} size={13} />
                  Connection Stats
                </div>
                <div style={{ display: 'grid', gap: '4px' }}>
                  <div>Total connected users: <span style={{ color: '#60a5fa' }}>{recipientStats.total_connections}</span></div>
                  {recipientStats.excluded?.no_marketing > 0 && (
                    <div style={{ color: '#fbbf24' }}>
                      <Icon name="warning" color="warning" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {recipientStats.excluded.no_marketing} users haven't enabled marketing emails
                    </div>
                  )}
                  {recipientStats.excluded?.no_email > 0 && (
                    <div style={{ color: '#f87171' }}>
                      <Icon name="error" color="error" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {recipientStats.excluded.no_email} users without email
                    </div>
                  )}
                  {recipientStats.excluded?.inactive > 0 && (
                    <div style={{ color: '#f87171' }}>
                      <Icon name="error" color="error" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {recipientStats.excluded.inactive} inactive users
                    </div>
                  )}
                  {recipientStats.excluded?.no_brand_match > 0 && (
                    <div style={{ color: '#fbbf24' }}>
                      <Icon name="warning" color="warning" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {recipientStats.excluded.no_brand_match} users don't match the selected brand
                    </div>
                  )}
                </div>
                
                {/* Debug: Show all connected users status */}
                {debugUsers.length > 0 && (
                  <div style={{ marginTop: '12px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb', paddingTop: '12px' }}>
                    <div style={{ fontWeight: '500', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', marginBottom: '8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="details" color="secondary" darkMode={darkMode} size={12} />
                      DEBUG: Connected Users Detail
                    </div>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '11px' }}>
                      {debugUsers.map((u, idx) => (
                        <div key={idx} style={{
                          padding: '6px 8px',
                          background: idx % 2 === 0
                            ? (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)')
                            : 'transparent',
                          borderRadius: '4px',
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '4px'
                        }}>
                          <div style={{ color: u.included ? '#4A8C18' : '#f87171' }}>
                            {u.username || `User #${u.user_id}`}
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {u.included ? (
                              <span style={{ color: '#4A8C18' }}>
                                <Icon name="success" color="success" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                Included
                              </span>
                            ) : (
                              <span style={{ color: '#f87171' }}>
                                {u.excluded_reason === 'no_marketing' && <><Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Marketing OFF</>}
                                {u.excluded_reason === 'no_email' && <><Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> No email</>}
                                {u.excluded_reason === 'inactive' && <><Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Inactive</>}
                                {u.excluded_reason === 'no_brand_match' && <><Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Brand mismatch</>}
                                {u.excluded_reason === 'no_user' && <><Icon name="error" color="error" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> No user data</>}
                              </span>
                            )}
                          </div>
                          <div style={{ color: darkMode ? 'rgba(255,255,255,0.4)' : '#6b7280', gridColumn: 'span 2', fontSize: '10px' }}>
                            Email: {u.email || 'N/A'} | Marketing: {u.marketing_reminder ? 'ON' : 'OFF'} | Active: {u.is_active ? 'Yes' : 'No'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recipients list preview */}
            {recipients.length > 0 && (
              <div>
                <div style={{
                  fontSize: '12px',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                  marginBottom: '8px'
                }}>
                  Preview (showing up to 10):
                </div>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  fontSize: '13px'
                }}>
                  {recipients.slice(0, 10).map((r, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 12px',
                        background: idx % 2 === 0
                          ? (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)')
                          : 'transparent',
                        borderRadius: '6px',
                        color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151'
                      }}
                    >
                      {r.name || r.email}
                    </div>
                  ))}
                  {recipients.length > 10 && (
                    <div style={{
                      padding: '8px 12px',
                      color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      +{recipients.length - 10} more...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview View */}
      {viewMode === 'preview' && (
        <div style={{
          background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            background: darkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon name="email" color="primary" darkMode={darkMode} size={20} />
            <div>
              <div style={{ fontSize: '14px', color: darkMode ? '#fff' : '#1f2937', fontWeight: '600' }}>
                Email Preview
              </div>
              <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                This is how your email will appear to recipients
              </div>
            </div>
          </div>
          
          <div style={{ 
            padding: '32px 24px',
            background: darkMode
              ? 'linear-gradient(135deg, #2d2d4a 0%, #1a1a2e 100%)'
              : 'linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)'
          }}>
            <EmailPreview 
              subject={emailSubject}
              content={editorContent}
              companyName={companyName}
            />
          </div>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div style={{
          background: darkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            background: darkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: 0, color: darkMode ? '#fff' : '#1f2937', fontSize: '16px' }}>
              <Icon name="details" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              Campaign History
            </h3>
          </div>
          
          {campaignHistory.length === 0 ? (
            <div style={{
              padding: '60px 20px',
              textAlign: 'center',
              color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <Icon name="email" color="muted" darkMode={darkMode} size={40} />
              </div>
              <p>No campaigns yet. Create your first campaign above!</p>
            </div>
          ) : (
            <div style={{ padding: '20px' }}>
              {campaignHistory.map((campaign) => (
                <div
                  key={campaign.id}
                  style={{
                    padding: '16px',
                    background: darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
                    borderRadius: '12px',
                    marginBottom: '12px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: darkMode ? '#fff' : '#1f2937',
                        marginBottom: '4px'
                      }}>
                        {campaign.subject}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
                      }}>
                        {getMonthName(campaign.month)} {campaign.year}
                      </div>
                    </div>
                    {getStatusBadge(campaign.status)}
                  </div>
                  
                  {campaign.status === 'sent' && (
                    <div style={{
                      display: 'flex',
                      gap: '24px',
                      padding: '12px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}>
                      <div>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Sent: </span>
                        <span style={{ color: '#4A8C18', fontWeight: '600' }}>
                          {campaign.sent_count}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Failed: </span>
                        <span style={{ color: '#f87171', fontWeight: '600' }}>
                          {campaign.failed_count}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Date: </span>
                        <span style={{ color: darkMode ? '#fff' : '#1f2937' }}>
                          {new Date(campaign.sent_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {campaign.status === 'rejected' && campaign.rejection_reason && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#f87171'
                    }}>
                      <strong>Rejection reason:</strong> {campaign.rejection_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info box */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '12px',
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start'
      }}>
        <Icon name="info" color="info" darkMode={darkMode} size={20} />
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#60a5fa', fontSize: '14px' }}>
            How it works
          </h4>
          <ul style={{
            margin: 0,
            padding: '0 0 0 20px',
            color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
            fontSize: '13px',
            lineHeight: '1.8'
          }}>
            <li>You can send <strong>1 promotional email per month</strong> to your customers</li>
            <li>Subject will automatically be prefixed with <strong>"Frame-Book Promo:"</strong></li>
            <li>Create your email using the editor with bold, italic, underline, and font size options</li>
            <li>Filter recipients by bike brand or send to all customers</li>
            <li>After submission, an administrator will review your content before it's sent</li>
            <li>Only customers who opted-in for marketing emails will receive your campaign</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MarketingCampaignManager;
